import React, { createContext, useContext, useEffect, useState } from 'react';
import Lottie from 'lottie-react';
import animationData from './assets/elevatemart_loading.json';
import { LICENSE_CONFIG } from './license.config';

const CACHE_KEY = 'license_cache_v1';
const CACHE_TTL_MS = 6 * 60 * 60 * 1000;

const LicenseContext = createContext(null);
export const useLicense = () => useContext(LicenseContext);

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.cachedAt > CACHE_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function writeCache(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ cachedAt: Date.now(), data }));
  } catch {}
}

async function fetchLicenseStatus() {
  const res = await fetch(LICENSE_CONFIG.apiUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${LICENSE_CONFIG.apiKey}` },
    body: JSON.stringify({ domain: LICENSE_CONFIG.domain }),
  });
  if (!res.ok) throw new Error('verify failed');
  return res.json();
}

export function LicenseGuard({ children }) {
  const [state, setState] = useState({ loading: true, data: readCache() });

  useEffect(() => {
    let cancelled = false;
    fetchLicenseStatus()
      .then((data) => {
        if (cancelled) return;
        writeCache(data);
        setState({ loading: false, data });
      })
      .catch(() => {
        if (cancelled) return;
        setState((prev) => ({ loading: false, data: prev.data }));
      });
    return () => { cancelled = true; };
  }, []);

  if (state.loading && !state.data) return <LoadingScreen />;

  const isBlocked = !state.data || state.data.status === 'blocked' || state.data.status === 'expired';
  if (isBlocked) return <PaymentRequiredScreen licenseInfo={state.data} />;

  return <LicenseContext.Provider value={state.data}>{children}</LicenseContext.Provider>;
}

// App.js এর FullScreenLoader এর সাথে হুবহু একই: সাদা background + same Lottie
function LoadingScreen() {
  return (
    <div style={s.loader}>
      <Lottie
        animationData={animationData}
        loop={true}
        autoplay={true}
        style={{ width: 300, height: 300 }}
      />
    </div>
  );
}

function PaymentRequiredScreen({ licenseInfo }) {
  const message = licenseInfo?.message || 'আপনার সাবস্ক্রিপশনের মেয়াদ শেষ হয়ে গেছে।';
  const waNumber = LICENSE_CONFIG.supportWhatsApp;
  const waText = encodeURIComponent(`আসসালামু আলাইকুম, ${LICENSE_CONFIG.domain} এর সাবস্ক্রিপশন renew করতে চাই।`);

  return (
    <div style={s.center}>
      <div style={s.card}>
        <h2 style={s.title}>সার্ভিস সাময়িকভাবে বন্ধ আছে</h2>
        <p style={s.message}>{message}</p>
        <a href={`https://wa.me/${waNumber}?text=${waText}`} target="_blank" rel="noreferrer" style={s.payButton}>
          এখনই Renew করুন (WhatsApp)
        </a>
      </div>
    </div>
  );
}

const s = {
  loader: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', width: '100vw', position: 'fixed', top: 0, left: 0, background: '#ffffff', zIndex: 9999 },
  center: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a', fontFamily: 'system-ui, sans-serif' },
  card: { maxWidth: 420, padding: '32px 28px', background: '#1e293b', borderRadius: 14, textAlign: 'center', color: '#f1f5f9' },
  title: { margin: '0 0 12px', fontSize: 20, fontWeight: 700 },
  message: { margin: '0 0 20px', color: '#cbd5e1', fontSize: 15 },
  payButton: { display: 'inline-block', padding: '12px 24px', background: '#22c55e', color: '#fff', borderRadius: 8, textDecoration: 'none', fontWeight: 600 },
};
